import DefaultApiService from "./default";
import { api, authHeader } from "../configs/axiosConfigs";

/**
 * Phase 7.4.1 — Contractor Portal API (existing contractor-area endpoints only).
 * Ownership is enforced by the backend via the authenticated user — never send contractor IDs.
 */

const BASE = {
  dashboard: "SLN/Contractor/ContractorDashboard",
  master: "SLN/Contractor/ContractorMasterContracts",
  annexes: "SLN/Contractor/ContractorProjectAnnexes",
  executions: "SLN/Contractor/ContractorExecutions",
  assignments: "SLN/Contractor/ProjectAssignments",
  portalWorkspace: "SLN/Contractor/ContractorPortalWorkspace",
};

const pick = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const unwrap = (res) => pick(res, "data", "Data") ?? null;

const isOk = (res) => {
  if (res == null) return false;
  if (res.isSuccess === false || res.IsSuccess === false) return false;
  return true;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const messageOf = (res, fallback) =>
  pick(res, "message", "Message") || fallback || "خطا در دریافت اطلاعات";

const toResult = (ok, data, message) => ({
  isSuccess: ok,
  IsSuccess: ok,
  message: message || null,
  Message: message || null,
  data,
  Data: data,
});

const ACTIVE_EXECUTION_STATUSES = new Set([1, 2, 3, 4, "1", "2", "3", "4"]);
const PENDING_PAYMENT_STATUSES = new Set([1, 2, "1", "2", "Requested", "Approved"]);
const ACTIVE_CONTRACT_STATUSES = new Set([7, "7", "Active"]);

const normalizeMaster = (raw) => {
  if (!raw) return null;
  return {
    contractId: pick(raw, "contractId", "ContractId"),
    contractNumber: pick(raw, "contractNumber", "ContractNumber"),
    status: pick(raw, "status", "Status"),
    statusTitle: pick(raw, "statusTitle", "StatusTitle"),
    guaranteeStatus: pick(raw, "guaranteeStatus", "GuaranteeStatus"),
    guaranteeStatusTitle: pick(
      raw,
      "guaranteeStatusTitle",
      "GuaranteeStatusTitle"
    ),
    signedAt:
      pick(raw, "contractorSignedAt", "ContractorSignedAt") ||
      pick(raw, "solarentSignedAt", "SolarentSignedAt"),
    contractorSignedAt: pick(raw, "contractorSignedAt", "ContractorSignedAt"),
    solarentSignedAt: pick(raw, "solarentSignedAt", "SolarentSignedAt"),
    activatedAt: pick(raw, "activatedAt", "ActivatedAt"),
    createOn: pick(raw, "createOn", "CreateOn"),
    finalPdfUrl: pick(raw, "finalPdfUrl", "FinalPdfUrl"),
    signedPdfUrl: pick(raw, "signedPdfUrl", "SignedPdfUrl"),
    versionNumber: pick(raw, "versionNumber", "VersionNumber"),
    verificationCode: pick(raw, "verificationCode", "VerificationCode"),
    pdfSha256: pick(raw, "pdfSha256", "PdfSha256"),
    canSendContractorOtp: pick(raw, "canSendContractorOtp", "CanSendContractorOtp"),
    canVerifyContractorSign: pick(
      raw,
      "canVerifyContractorSign",
      "CanVerifyContractorSign"
    ),
    acceptanceText: pick(raw, "acceptanceText", "AcceptanceText"),
    postSignMessage: pick(raw, "postSignMessage", "PostSignMessage"),
    contractorSignerMaskedMobile: pick(
      raw,
      "contractorSignerMaskedMobile",
      "ContractorSignerMaskedMobile"
    ),
    canUploadGuaranteeDocument: pick(
      raw,
      "canUploadGuaranteeDocument",
      "CanUploadGuaranteeDocument"
    ),
    canViewGuaranteeDocument: pick(
      raw,
      "canViewGuaranteeDocument",
      "CanViewGuaranteeDocument"
    ),
    hasActiveGuaranteeDocument: pick(
      raw,
      "hasActiveGuaranteeDocument",
      "HasActiveGuaranteeDocument"
    ),
    guaranteeAmountIrr: pick(raw, "guaranteeAmountIrr", "GuaranteeAmountIrr"),
    guaranteeType: pick(raw, "guaranteeType", "GuaranteeType"),
    guaranteeSayadiId: pick(raw, "guaranteeSayadiId", "GuaranteeSayadiId"),
    guaranteeChequeSerial: pick(
      raw,
      "guaranteeChequeSerial",
      "GuaranteeChequeSerial"
    ),
    guaranteeBankName: pick(raw, "guaranteeBankName", "GuaranteeBankName"),
    guaranteeAccountNumber: pick(
      raw,
      "guaranteeAccountNumber",
      "GuaranteeAccountNumber"
    ),
    guaranteeBranchName: pick(raw, "guaranteeBranchName", "GuaranteeBranchName"),
    guaranteeIssuerName: pick(raw, "guaranteeIssuerName", "GuaranteeIssuerName"),
    guaranteeChequeDate: pick(raw, "guaranteeChequeDate", "GuaranteeChequeDate"),
    guaranteeChequeDateFa: pick(
      raw,
      "guaranteeChequeDateFa",
      "GuaranteeChequeDateFa"
    ),
    physicalGuaranteeReceivedAt: pick(
      raw,
      "physicalGuaranteeReceivedAt",
      "PhysicalGuaranteeReceivedAt"
    ),
    hasGuaranteeChequeDetails: pick(
      raw,
      "hasGuaranteeChequeDetails",
      "HasGuaranteeChequeDetails"
    ),
    solarentOfficeMailingAddress: pick(
      raw,
      "solarentOfficeMailingAddress",
      "SolarentOfficeMailingAddress"
    ),
  };
};

/** Phase 7.7.1 — Signed(6)/AwaitingGuarantee(11) without Approved guarantee(4) */
const isAwaitingGuaranteeUnlock = (master) => {
  if (!master) return false;
  const status = master.status;
  const g = master.guaranteeStatus;
  const postSign =
    status === 6 ||
    status === 11 ||
    status === "6" ||
    status === "11" ||
    status === "Signed" ||
    status === "AwaitingGuarantee";
  const guaranteeApproved =
    g === 4 || g === "4" || g === "Approved";
  return postSign && !guaranteeApproved;
};

const normalizeAnnex = (raw) => {
  if (!raw) return null;
  return {
    annexId: pick(raw, "annexId", "AnnexId"),
    requestId: pick(raw, "requestId", "RequestId"),
    projectCode: pick(raw, "projectCode", "ProjectCode"),
    annexNumber: pick(raw, "annexNumber", "AnnexNumber"),
    status: pick(raw, "status", "Status"),
    statusTitle: pick(raw, "statusTitle", "StatusTitle"),
    versionNumber: pick(raw, "versionNumber", "VersionNumber"),
    createdAt: pick(raw, "createdAt", "CreatedAt"),
    activatedAt: pick(raw, "activatedAt", "ActivatedAt"),
    contractorContractAmount: pick(
      raw,
      "contractorContractAmount",
      "ContractorContractAmount"
    ),
    finalPdfUrl: pick(raw, "finalPdfUrl", "FinalPdfUrl"),
    signedPdfUrl: pick(raw, "signedPdfUrl", "SignedPdfUrl"),
  };
};

const normalizeExecutionListItem = (raw) => {
  if (!raw) return null;
  return {
    id: pick(raw, "id", "Id"),
    annexId: pick(raw, "contractorProjectAnnexId", "ContractorProjectAnnexId"),
    requestId: pick(raw, "requestId", "RequestId"),
    projectCode: pick(raw, "projectCode", "ProjectCode"),
    status: pick(raw, "status", "Status"),
    statusTitle: pick(raw, "statusTitle", "StatusTitle"),
    startedAt: pick(raw, "startedAt", "StartedAt"),
    createdAt: pick(raw, "createdAt", "CreatedAt"),
    contractorContractAmount: pick(
      raw,
      "contractorContractAmount",
      "ContractorContractAmount"
    ),
  };
};

const normalizeAssignment = (raw) => {
  if (!raw) return null;
  return {
    id: pick(raw, "id", "Id"),
    requestId: pick(raw, "requestId", "RequestId"),
    projectCode:
      pick(raw, "requestReferenceId", "RequestReferenceId") ||
      pick(raw, "requestTitle", "RequestTitle"),
    city: pick(raw, "cityName", "CityName"),
    capacity: pick(raw, "plantCapacityTitle", "PlantCapacityTitle"),
    status: pick(raw, "projectAssignmentStatus", "ProjectAssignmentStatus"),
    statusTitle: pick(
      raw,
      "projectAssignmentStatusTitle",
      "ProjectAssignmentStatusTitle"
    ),
  };
};

const latestProgressPercent = (executionDetail) => {
  const reports = asArray(
    pick(executionDetail, "progressReports", "ProgressReports")
  );
  if (!reports.length) return null;
  const sorted = [...reports].sort((a, b) => {
    const aAt = new Date(pick(a, "submittedAt", "SubmittedAt") || 0).getTime();
    const bAt = new Date(pick(b, "submittedAt", "SubmittedAt") || 0).getTime();
    return bAt - aAt;
  });
  const approved = sorted.find((r) => {
    const st = pick(r, "status", "Status");
    return st === 2 || st === "2" || st === "Approved";
  });
  const chosen = approved || sorted[0];
  const pct = pick(chosen, "percentComplete", "PercentComplete");
  return pct == null ? null : Number(pct);
};

const countPendingPayments = (executionDetail) => {
  const requests = asArray(
    pick(executionDetail, "paymentRequests", "PaymentRequests")
  );
  return requests.filter((r) =>
    PENDING_PAYMENT_STATUSES.has(pick(r, "status", "Status"))
  ).length;
};

const fetchExecutionDetail = async (executionId) => {
  const res = await DefaultApiService.getApi(`${BASE.executions}/GetById`, {
    id: executionId,
  });
  if (!isOk(res)) return null;
  return unwrap(res);
};

const fetchAnnexDetail = async (annexId) => {
  const res = await DefaultApiService.getApi(`${BASE.annexes}/GetById`, {
    id: annexId,
  });
  if (!isOk(res)) return null;
  return normalizeAnnex(unwrap(res));
};

const buildExecutionStatusSummary = (executions) => {
  const map = new Map();
  executions.forEach((item) => {
    const key = item.statusTitle || String(item.status ?? "نامشخص");
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries()).map(([statusTitle, count]) => ({
    statusTitle,
    count,
  }));
};

const ContractorPortalApi = {
  /**
   * Composes legacy dashboard statistics with Contract Engine execution details.
   */
  getDashboardSummary: async () => {
    try {
      const [statsRes, executionsRes, masterRes, annexesRes] =
        await Promise.all([
          DefaultApiService.getCustomeApi(`${BASE.dashboard}/GetStatistics`),
          DefaultApiService.getCustomeApi(`${BASE.executions}/MyExecutions`),
          DefaultApiService.getCustomeApi(`${BASE.master}/Current`),
          DefaultApiService.getCustomeApi(`${BASE.annexes}/AssignedAnnexes`),
        ]);

      const statsOk = isOk(statsRes);
      const executionsOk = isOk(executionsRes);
      if (!statsOk && !executionsOk) {
        return toResult(
          false,
          null,
          messageOf(statsRes || executionsRes, "دریافت خلاصه داشبورد ناموفق بود.")
        );
      }

      const stats = unwrap(statsRes) || {};
      const executions = asArray(unwrap(executionsRes)).map(normalizeExecutionListItem).filter(Boolean);
      const master = isOk(masterRes) ? normalizeMaster(unwrap(masterRes)) : null;
      const annexes = isOk(annexesRes)
        ? asArray(unwrap(annexesRes)).map(normalizeAnnex).filter(Boolean)
        : [];

      const details = await Promise.all(
        executions.map((e) => (e?.id != null ? fetchExecutionDetail(e.id) : null))
      );

      let pendingPayments = 0;
      let openDefects = 0;
      details.forEach((detail) => {
        if (!detail) return;
        pendingPayments += countPendingPayments(detail);
        if (pick(detail, "hasOpenDefects", "HasOpenDefects")) openDefects += 1;
      });

      const activeAnnexes = annexes.filter((a) =>
        ACTIVE_CONTRACT_STATUSES.has(a.status)
      ).length;
      const activeMaster = master && ACTIVE_CONTRACT_STATUSES.has(master.status) ? 1 : 0;
      const engineActiveContracts = activeMaster + activeAnnexes;

      const activeProjects = executions.filter((e) =>
        ACTIVE_EXECUTION_STATUSES.has(e.status)
      ).length;

      const data = {
        activeContracts:
          engineActiveContracts > 0
            ? engineActiveContracts
            : Number(pick(stats, "activeContracts", "ActiveContracts") || 0),
        activeProjects:
          activeProjects > 0
            ? activeProjects
            : Number(pick(stats, "projectsCount", "ProjectsCount") || 0),
        pendingPayments,
        openDefects,
        executionStatusSummary: buildExecutionStatusSummary(executions),
        rawStatistics: stats,
        master,
        awaitingGuaranteeUnlock: isAwaitingGuaranteeUnlock(master),
        operationalAccessEnabled: !isAwaitingGuaranteeUnlock(master) &&
          ACTIVE_CONTRACT_STATUSES.has(master?.status),
      };

      return toResult(true, data, null);
    } catch (err) {
      return toResult(
        false,
        null,
        err?.message || "خطا در دریافت خلاصه داشبورد پیمانکار."
      );
    }
  },

  getContracts: async () => {
    try {
      const [masterRes, annexesRes] = await Promise.all([
        DefaultApiService.getCustomeApi(`${BASE.master}/Current`),
        DefaultApiService.getCustomeApi(`${BASE.annexes}/AssignedAnnexes`),
      ]);

      if (!isOk(masterRes) && !isOk(annexesRes)) {
        return toResult(
          false,
          null,
          messageOf(masterRes || annexesRes, "دریافت قراردادها ناموفق بود.")
        );
      }

      const master = isOk(masterRes) ? normalizeMaster(unwrap(masterRes)) : null;
      const listItems = isOk(annexesRes)
        ? asArray(unwrap(annexesRes)).map(normalizeAnnex).filter(Boolean)
        : [];

      const annexes = await Promise.all(
        listItems.map(async (item) => {
          const detail = item.annexId != null ? await fetchAnnexDetail(item.annexId) : null;
          return {
            ...item,
            ...(detail || {}),
            contractorContractAmount:
              detail?.contractorContractAmount ?? item.contractorContractAmount ?? null,
            finalPdfUrl: detail?.finalPdfUrl ?? item.finalPdfUrl ?? null,
            signedPdfUrl: detail?.signedPdfUrl ?? item.signedPdfUrl ?? null,
          };
        })
      );

      return toResult(true, { master, annexes }, null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت قراردادها.");
    }
  },

  getProjects: async () => {
    try {
      const [executionsRes, assignmentsRes] = await Promise.all([
        DefaultApiService.getCustomeApi(`${BASE.executions}/MyExecutions`),
        DefaultApiService.getCustomeApi(
          `${BASE.assignments}/GetMyAssignments`
        ),
      ]);

      if (!isOk(executionsRes)) {
        return toResult(
          false,
          null,
          messageOf(executionsRes, "دریافت پروژه‌ها ناموفق بود.")
        );
      }

      const executions = asArray(unwrap(executionsRes))
        .map(normalizeExecutionListItem)
        .filter(Boolean);
      const assignments = isOk(assignmentsRes)
        ? asArray(unwrap(assignmentsRes)).map(normalizeAssignment).filter(Boolean)
        : [];

      const assignmentByRequestId = new Map(
        assignments.map((a) => [String(a.requestId), a])
      );

      const details = await Promise.all(
        executions.map((e) => (e?.id != null ? fetchExecutionDetail(e.id) : null))
      );

      const projects = executions.map((exec, index) => {
        const detail = details[index];
        const assignment = assignmentByRequestId.get(String(exec.requestId));
        return {
          id: exec.id,
          projectCode: exec.projectCode,
          requestId: exec.requestId,
          capacity: assignment?.capacity || null,
          city: assignment?.city || null,
          executionStatus: exec.status,
          executionStatusTitle: exec.statusTitle,
          progressPercent: latestProgressPercent(detail),
          contractAmount:
            pick(detail, "contractorContractAmount", "ContractorContractAmount") ??
            exec.contractorContractAmount ??
            null,
          hasOpenDefects: Boolean(
            pick(detail, "hasOpenDefects", "HasOpenDefects")
          ),
        };
      });

      return toResult(true, { projects }, null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت پروژه‌ها.");
    }
  },

  /**
   * Project detail foundation — execution summary for authenticated contractor only.
   */
  getProjectDetail: async (executionId) => {
    try {
      if (executionId == null || executionId === "") {
        return toResult(false, null, "شناسه پروژه نامعتبر است.");
      }

      const detailRes = await DefaultApiService.getApi(
        `${BASE.executions}/GetById`,
        { id: executionId }
      );
      if (!isOk(detailRes) || !unwrap(detailRes)) {
        return toResult(
          false,
          null,
          messageOf(detailRes, "پروژه یافت نشد یا دسترسی ندارید.")
        );
      }

      const detail = unwrap(detailRes);
      const annexId = pick(
        detail,
        "contractorProjectAnnexId",
        "ContractorProjectAnnexId"
      );
      const annex =
        annexId != null ? await fetchAnnexDetail(annexId) : null;

      let assignment = null;
      const requestId = pick(detail, "requestId", "RequestId");
      try {
        const assignmentsRes = await DefaultApiService.getCustomeApi(
          `${BASE.assignments}/GetMyAssignments`
        );
        if (isOk(assignmentsRes)) {
          assignment =
            asArray(unwrap(assignmentsRes))
              .map(normalizeAssignment)
              .find((a) => String(a.requestId) === String(requestId)) || null;
        }
      } catch {
        assignment = null;
      }

      const data = {
        project: {
          id: pick(detail, "id", "Id"),
          annexId,
          projectCode: pick(detail, "projectCode", "ProjectCode"),
          requestId,
          capacity: assignment?.capacity || null,
          city: assignment?.city || null,
          executionStatus: pick(detail, "status", "Status"),
          executionStatusTitle: pick(detail, "statusTitle", "StatusTitle"),
          progressPercent: latestProgressPercent(detail),
          contractAmount: pick(
            detail,
            "contractorContractAmount",
            "ContractorContractAmount"
          ),
          startedAt: pick(detail, "startedAt", "StartedAt"),
          completedAt: pick(detail, "completedAt", "CompletedAt"),
          hasOpenDefects: Boolean(
            pick(detail, "hasOpenDefects", "HasOpenDefects")
          ),
          finalAcceptance: Boolean(
            pick(detail, "finalAcceptance", "FinalAcceptance")
          ),
          warrantyCompleted: Boolean(
            pick(detail, "warrantyCompleted", "WarrantyCompleted")
          ),
          socialSecurityClearance: Boolean(
            pick(detail, "socialSecurityClearance", "SocialSecurityClearance")
          ),
        },
        contract: annex
          ? {
              annexId: annex.annexId,
              annexNumber: annex.annexNumber,
              statusTitle: annex.statusTitle,
              contractorContractAmount: annex.contractorContractAmount,
              activatedAt: annex.activatedAt,
            }
          : null,
      };

      return toResult(true, data, null);
    } catch (err) {
      return toResult(
        false,
        null,
        err?.message || "خطا در دریافت جزئیات پروژه."
      );
    }
  },

  getProgress: async (executionId) => {
    try {
      const detailRes = await DefaultApiService.getApi(
        `${BASE.executions}/GetById`,
        { id: executionId }
      );
      if (!isOk(detailRes) || !unwrap(detailRes)) {
        return toResult(
          false,
          null,
          messageOf(detailRes, "دریافت پیشرفت ناموفق بود.")
        );
      }
      const detail = unwrap(detailRes);
      const reports = asArray(
        pick(detail, "progressReports", "ProgressReports")
      ).map((r) => ({
        id: pick(r, "id", "Id"),
        percentComplete: pick(r, "percentComplete", "PercentComplete"),
        description: pick(r, "description", "Description"),
        status: pick(r, "status", "Status"),
        statusTitle: pick(r, "statusTitle", "StatusTitle"),
        submittedAt: pick(r, "submittedAt", "SubmittedAt"),
        reviewedAt: pick(r, "reviewedAt", "ReviewedAt"),
        reviewComment: pick(r, "reviewComment", "ReviewComment"),
        attachments: asArray(pick(r, "attachments", "Attachments")),
      }));

      return toResult(true, {
        executionId: pick(detail, "id", "Id"),
        annexId: pick(
          detail,
          "contractorProjectAnnexId",
          "ContractorProjectAnnexId"
        ),
        currentProgressPercent: latestProgressPercent(detail),
        reports,
        canSubmit:
          pick(detail, "status", "Status") !== 5 &&
          pick(detail, "status", "Status") !== 6 &&
          pick(detail, "status", "Status") !== "Closed" &&
          pick(detail, "status", "Status") !== "Cancelled",
      });
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت پیشرفت.");
    }
  },

  submitProgress: async (annexId, payload) => {
    try {
      const body = {
        PercentComplete: Number(payload?.percentComplete ?? payload?.PercentComplete),
        Description: payload?.description ?? payload?.Description ?? "",
        Attachments: asArray(payload?.attachments ?? payload?.Attachments).map(
          (a) => ({
            FileName: a.fileName ?? a.FileName,
            FilePath: a.filePath ?? a.FilePath,
            MimeType: a.mimeType ?? a.MimeType,
            Sha256: a.sha256 ?? a.Sha256,
            Content: a.content ?? a.Content,
          })
        ),
      };
      const res = await DefaultApiService.postApi(
        `${BASE.executions}/SubmitProgress?annexId=${encodeURIComponent(annexId)}`,
        body
      );
      if (!isOk(res)) {
        return toResult(false, null, messageOf(res, "ثبت پیشرفت ناموفق بود."));
      }
      return toResult(true, unwrap(res), null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در ثبت پیشرفت.");
    }
  },

  getDefects: async (executionId) => {
    try {
      const res = await DefaultApiService.getApi(
        `${BASE.executions}/OpenDefects`,
        { executionId }
      );
      if (!isOk(res)) {
        return toResult(false, null, messageOf(res, "دریافت ایرادات ناموفق بود."));
      }
      const defects = asArray(unwrap(res)).map((d) => ({
        id: pick(d, "id", "Id"),
        title: pick(d, "title", "Title"),
        description: pick(d, "description", "Description"),
        severity: pick(d, "severity", "Severity"),
        severityTitle: pick(d, "severityTitle", "SeverityTitle"),
        status: pick(d, "status", "Status"),
        statusTitle: pick(d, "statusTitle", "StatusTitle"),
        assignedToUserId: pick(d, "assignedToUserId", "AssignedToUserId"),
        createdAt: pick(d, "createdAt", "CreatedAt"),
        resolvedAt: pick(d, "resolvedAt", "ResolvedAt"),
        attachments: asArray(pick(d, "attachments", "Attachments")),
        canResolve: (() => {
          const st = pick(d, "status", "Status");
          return st !== 4 && st !== 5 && st !== "Closed" && st !== "Rejected";
        })(),
      }));
      return toResult(true, { defects }, null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت ایرادات.");
    }
  },

  resolveDefect: async (defectId) => {
    try {
      const res = await DefaultApiService.postApi(
        `${BASE.executions}/ResolveAssignedDefect?defectId=${encodeURIComponent(
          defectId
        )}`,
        {}
      );
      if (!isOk(res)) {
        return toResult(false, null, messageOf(res, "رفع ایراد ناموفق بود."));
      }
      return toResult(true, unwrap(res), null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در رفع ایراد.");
    }
  },

  /**
   * Project-scoped payments from GetById (no payment lifecycle changes).
   * Without executionId: FE composition across MyExecutions.
   */
  getPayments: async (executionId) => {
    try {
      const mapPayments = (detail) =>
        asArray(pick(detail, "paymentRequests", "PaymentRequests")).map((p) => ({
          id: pick(p, "id", "Id"),
          executionInstanceId: pick(p, "executionInstanceId", "ExecutionInstanceId"),
          projectCode: pick(detail, "projectCode", "ProjectCode"),
          grossAmount: pick(p, "grossAmount", "GrossAmount"),
          retentionAmount: pick(p, "retentionAmount", "RetentionAmount"),
          insuranceRetentionAmount: pick(
            p,
            "insuranceRetentionAmount",
            "InsuranceRetentionAmount"
          ),
          deductionAmount: pick(p, "deductionAmount", "DeductionAmount"),
          netPayableAmount: pick(p, "netPayableAmount", "NetPayableAmount"),
          status: pick(p, "status", "Status"),
          statusTitle: pick(p, "statusTitle", "StatusTitle"),
          requestedAt: pick(p, "requestedAt", "RequestedAt"),
          notes: pick(p, "notes", "Notes"),
        }));

      if (executionId != null && executionId !== "") {
        const detailRes = await DefaultApiService.getApi(
          `${BASE.executions}/GetById`,
          { id: executionId }
        );
        if (!isOk(detailRes) || !unwrap(detailRes)) {
          return toResult(
            false,
            null,
            messageOf(detailRes, "دریافت پرداخت‌ها ناموفق بود.")
          );
        }
        const detail = unwrap(detailRes);
        return toResult(true, { payments: mapPayments(detail) }, null);
      }

      const listRes = await DefaultApiService.getCustomeApi(
        `${BASE.executions}/MyExecutions`
      );
      if (!isOk(listRes)) {
        return toResult(
          false,
          null,
          messageOf(listRes, "دریافت پرداخت‌ها ناموفق بود.")
        );
      }
      const executions = asArray(unwrap(listRes));
      const details = await Promise.all(
        executions.map((e) => {
          const id = pick(e, "id", "Id");
          return id != null ? fetchExecutionDetail(id) : null;
        })
      );
      const payments = details.flatMap((d) => (d ? mapPayments(d) : []));
      return toResult(true, { payments }, null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت پرداخت‌ها.");
    }
  },

  getAcceptance: async (executionId) => {
    try {
      const res = await DefaultApiService.getApi(
        `${BASE.portalWorkspace}/GetAcceptance`,
        { executionId }
      );
      if (!isOk(res)) {
        return toResult(
          false,
          null,
          messageOf(res, "دریافت وضعیت پذیرش ناموفق بود.")
        );
      }
      const raw = unwrap(res) || {};
      const checklist = asArray(pick(raw, "checklist", "Checklist")).map((c) => ({
        itemCode: pick(c, "itemCode", "ItemCode"),
        itemTitle: pick(c, "itemTitle", "ItemTitle"),
        isRequired: Boolean(pick(c, "isRequired", "IsRequired")),
        isPassed: Boolean(pick(c, "isPassed", "IsPassed")),
        notes: pick(c, "notes", "Notes"),
      }));
      return toResult(true, {
        executionInstanceId: pick(raw, "executionInstanceId", "ExecutionInstanceId"),
        finalAcceptance: Boolean(pick(raw, "finalAcceptance", "FinalAcceptance")),
        warrantyCompleted: Boolean(
          pick(raw, "warrantyCompleted", "WarrantyCompleted")
        ),
        socialSecurityClearance: Boolean(
          pick(raw, "socialSecurityClearance", "SocialSecurityClearance")
        ),
        hasOpenDefects: Boolean(pick(raw, "hasOpenDefects", "HasOpenDefects")),
        checklist,
        missingRequirements: asArray(
          pick(raw, "missingRequirements", "MissingRequirements")
        ),
        feedback: pick(raw, "feedback", "Feedback"),
        latest: pick(raw, "latest", "Latest"),
        canApprove: false,
      });
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت پذیرش.");
    }
  },

  /** Kept as stub until profile phase. */
  getProfile: async () =>
    toResult(
      false,
      null,
      "TODO: contractorPortalApi.getProfile wiring is deferred to a later phase."
    ),

  getMasterContractById: async (contractId) => {
    try {
      const res = await DefaultApiService.getApi(`${BASE.master}/GetById`, {
        contractId,
      });
      if (!isOk(res)) {
        return toResult(false, null, messageOf(res, "دریافت قرارداد ناموفق بود."));
      }
      const data = normalizeMaster(unwrap(res));
      if (!data?.contractId) {
        return toResult(false, null, messageOf(res, "قرارداد یافت نشد."));
      }
      return toResult(true, data, null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دریافت قرارداد.");
    }
  },

  sendMasterContractOtp: async (contractId) => {
    try {
      const res = await DefaultApiService.postApi(
        `${BASE.master}/SendOtp?contractId=${contractId}`,
        {}
      );
      if (!isOk(res)) {
        return toResult(false, null, messageOf(res, "ارسال کد تأیید ناموفق بود."));
      }
      const raw = unwrap(res) || {};
      return toResult(
        true,
        {
          maskedMobile: pick(raw, "maskedMobile", "MaskedMobile"),
          message: pick(raw, "message", "Message"),
          status: pick(raw, "status", "Status"),
          statusTitle: pick(raw, "statusTitle", "StatusTitle"),
        },
        null
      );
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در ارسال کد تأیید.");
    }
  },

  verifyMasterContractSign: async (contractId, verifyCode) => {
    try {
      const res = await DefaultApiService.postApi(
        `${BASE.master}/VerifySign?contractId=${contractId}`,
        { verifyCode }
      );
      if (!isOk(res)) {
        return toResult(false, null, messageOf(res, "تأیید امضا ناموفق بود."));
      }
      return toResult(true, unwrap(res), null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در تأیید امضا.");
    }
  },

  uploadGuaranteeDocument: async (contractId, file, chequeInfo = {}) => {
    try {
      if (!file) {
        return toResult(false, null, "فایل تصویر چک الزامی است.");
      }
      const formData = new FormData();
      // Must match [FromForm] IFormFile file — Content-Type stripped in axios interceptor.
      formData.append("file", file, file.name || "guarantee-document.jpg");
      formData.append("contractId", String(contractId));
      formData.append("sayadiId", String(chequeInfo.sayadiId || "").trim());
      formData.append("chequeSerial", String(chequeInfo.chequeSerial || "").trim());
      formData.append("bankName", String(chequeInfo.bankName || "").trim());
      formData.append("accountNumber", String(chequeInfo.accountNumber || "").trim());
      formData.append("branchName", String(chequeInfo.branchName || "").trim());
      formData.append("issuerName", String(chequeInfo.issuerName || "").trim());

      const res = await api.request({
        url: `${BASE.master}/UploadGuaranteeDocument?contractId=${encodeURIComponent(
          contractId
        )}`,
        method: "POST",
        data: formData,
        headers: authHeader(),
      });
      const body = res?.data;
      if (!isOk(body)) {
        return toResult(
          false,
          null,
          messageOf(body, "بارگذاری مدرک ضمانت ناموفق بود.")
        );
      }
      return toResult(true, unwrap(body), messageOf(body));
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        (typeof err?.response?.data === "string" ? err.response.data : null);
      return toResult(
        false,
        null,
        apiMsg || err?.message || "خطا در بارگذاری مدرک ضمانت."
      );
    }
  },

  downloadGuaranteeDocument: async (contractId) => {
    try {
      const res = await api.request({
        url: `${BASE.master}/GetGuaranteeDocument?contractId=${encodeURIComponent(
          contractId
        )}`,
        method: "GET",
        headers: authHeader(),
        responseType: "blob",
      });
      return toResult(true, res?.data, null);
    } catch (err) {
      return toResult(false, null, err?.message || "خطا در دانلود مدرک ضمانت.");
    }
  },

  /**
   * Authenticated PDF fetch — relative FinalPdfUrl/SignedPdfUrl must NOT be opened
   * on the frontend origin (causes SPA 404).
   */
  downloadMasterContractPdf: async (contractId, { preferSigned = true } = {}) => {
    if (!contractId) {
      return toResult(false, null, "شناسه قرارداد نامعتبر است.");
    }
    const tryEndpoints = preferSigned
      ? [
          `${BASE.master}/DownloadSignedPdf?contractId=${encodeURIComponent(contractId)}`,
          `${BASE.master}/DownloadPdf?contractId=${encodeURIComponent(contractId)}`,
        ]
      : [
          `${BASE.master}/DownloadPdf?contractId=${encodeURIComponent(contractId)}`,
          `${BASE.master}/DownloadSignedPdf?contractId=${encodeURIComponent(contractId)}`,
        ];

    let lastError = null;
    for (const url of tryEndpoints) {
      try {
        const res = await api.request({
          url,
          method: "GET",
          headers: authHeader(),
          responseType: "blob",
        });
        const blob = res?.data;
        if (!blob || !(blob instanceof Blob) || blob.size <= 0) {
          lastError = "فایل قرارداد خالی است.";
          continue;
        }
        // API may return JSON error as blob
        if (blob.type && blob.type.includes("application/json")) {
          const text = await blob.text();
          try {
            const parsed = JSON.parse(text);
            lastError = parsed?.message || parsed?.Message || "دریافت PDF ناموفق بود.";
          } catch {
            lastError = "دریافت PDF ناموفق بود.";
          }
          continue;
        }
        return toResult(true, blob, null);
      } catch (err) {
        lastError =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "خطا در دریافت فایل قرارداد.";
      }
    }
    return toResult(false, null, lastError || "فایل قرارداد در دسترس نیست.");
  },
};

export default ContractorPortalApi;

export const __testables = {
  pick,
  normalizeMaster,
  normalizeAnnex,
  normalizeExecutionListItem,
  latestProgressPercent,
  countPendingPayments,
  ACTIVE_CONTRACT_STATUSES,
};
